/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import handleClickInLegend from "components/charts/modern/helpers/handleClickInLegend";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesColumnOptions } from "highcharts/highstock";
import QuotaholdersHistoryQuery from "lib/graphql/queries/QuotaholdersHistoryQuery";
import type {
  IQuotaholdersHistoryQuery,
  IQuotaholdersHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/QuotaholdersHistoryQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import useTheme from "components/ui/useTheme";

interface QuotaholdersChartProps {
  tickerSymbols: string[];
}

export default function QuotaholdersChart({ tickerSymbols }: QuotaholdersChartProps) {
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const intl = useIntl();
  const theme = useTheme();

  const slateOrDarkSlate = theme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = theme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = theme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = theme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = theme === "dark" ? colors.slate[950] : colors.slate[200];
  const legendItemHoverColor = theme === "dark" ? colors.slate[400] : colors.slate[500];

  const selectedTickerSymbol = tickerSymbols[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }

  const { data, previousData, loading } = useQuery<IQuotaholdersHistoryQuery, IQuotaholdersHistoryQueryVariables>(
    QuotaholdersHistoryQuery,
    {
      variables: {
        tickerSymbol: selectedTickerSymbol,
      },
    },
  );

  const quotaholdersHistory = (loading && !data ? previousData?.quotaholdersHistory : data?.quotaholdersHistory) || [];

  if (!loading && quotaholdersHistory.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  const labels = {
    totalOfIndividuals: intl.formatMessage({
      defaultMessage: "Pessoa Física",
      id: "SDun3X",
    }),
    totalOfCompanies: intl.formatMessage({
      defaultMessage: "Pessoa Jurídica",
      id: "mfivRZ",
    }),
    totalOfFinancialInstitutions: intl.formatMessage({
      defaultMessage: "Instituições Financeiras",
      id: "lcPYYB",
    }),
    totalOfBanks: intl.formatMessage({
      defaultMessage: "Bancos",
      id: "W9x7Oh",
    }),
    totalOfInsurers: intl.formatMessage({
      defaultMessage: "Seguradoras",
      id: "Kmfnbv",
    }),
    totalOfRealEstateFunds: intl.formatMessage({
      defaultMessage: "Outros FIIs",
      id: "XrvMPY",
    }),
    totalOfQuotaholders: intl.formatMessage({
      defaultMessage: "Total de Cotistas",
      id: "Ky2ljL",
    }),
  };

  const nodes = [
    {
      name: labels.totalOfQuotaholders,
      field: "totalOfQuotaholders" as const,
      visible: true,
    },
    {
      name: labels.totalOfIndividuals,
      field: "totalOfIndividuals" as const,
      visible: false,
    },
    {
      name: labels.totalOfCompanies,
      field: "totalOfCompanies" as const,
      visible: false,
    },
    {
      name: labels.totalOfFinancialInstitutions,
      field: "totalOfFinancialInstitutions" as const,
      visible: false,
    },
    {
      name: labels.totalOfBanks,
      field: "totalOfBanks" as const,
      visible: false,
    },
    {
      name: labels.totalOfInsurers,
      field: "totalOfInsurers" as const,
      visible: false,
    },
    {
      name: labels.totalOfRealEstateFunds,
      field: "totalOfRealEstateFunds" as const,
      visible: false,
    },
  ];

  const series = nodes.map((node, index) => {
    const colorTheme = getColorThemeForIndex(index, theme);

    if (!colorTheme) {
      throw new Error("Theme not found.");
    }

    return {
      showInNavigator: true,
      type: "column",
      animation: false,
      name: node.name,
      visible: node.visible,
      color: colorTheme.color,
      lineColor: colorTheme.lineColor,
      lineWidth: 2,
      borderColor: colorTheme.lineColor,
      borderWidth: 1.5,
      stacking: "normal",
      states: {
        hover: {
          color: colorTheme.lineColor,
        },
      },
      data: quotaholdersHistory
        .filter((item) => item[node.field] !== null)
        .map((item) => ({
          x: parseISO(item.asOfDate).getTime(),
          y: item[node.field],
          custom: item,
        }))
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: colorTheme.color,
        secondaryColor: colorTheme.lineColor,
      },
    };
  }) as SeriesColumnOptions[];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    legend: {
      align: "left",
      enabled: true,
      padding: 0,
      alignColumns: true,
      itemDistance: 16,
      itemStyle: {
        color: labelsColor,
      },
      itemHoverStyle: {
        color: legendItemHoverColor,
      },
    },
    xAxis: {
      type: "datetime",
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      dateTimeLabelFormats: {
        millisecond: "%b '%y",
        second: "%b '%y",
        minute: "%b '%y",
        hour: "%b '%y",
        day: "%b '%y",
        week: "%b '%y",
        month: "%b '%y",
        year: "%Y",
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
    },
    yAxis: {
      type: "linear",
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          return intl.formatNumber(parseFloat(String(this.value)), {
            notation: "compact",
            compactDisplay: "short",
            minimumSignificantDigits: 1,
            maximumSignificantDigits: 4,
          });
        },
      },
      tickWidth: 0,
      showFirstLabel: true,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.x || !this.points || this.points.length === 0) {
          return "";
        }

        const pointData = this.points[0]?.point.options.custom;
        const visibleBreakdownPoints = this.points.filter(({ series }) => series.name !== labels.totalOfQuotaholders);
        const totalOfQuotaholdersPoint = this.points.find(({ series }) => series.name === labels.totalOfQuotaholders);
        const totalValue = totalOfQuotaholdersPoint?.y ?? pointData?.totalOfQuotaholders;
        const keyValues = [
          totalValue != null
            ? {
                key: labels.totalOfQuotaholders,
                value: intl.formatNumber(totalValue),
                yValue: totalValue,
                color: colors.slate[500],
              }
            : null,
          ...visibleBreakdownPoints
            .sort((a, b) => {
              return (b.y || 0) - (a.y || 0);
            })
            .map(({ series, y }) => {
              if (!y) {
                return null;
              }

              return {
                key: series.name,
                value: intl.formatNumber(y),
                yValue: y,
                color: series.userOptions.custom ? series.userOptions.custom.secondaryColor : undefined,
              };
            }),
        ].filter(notUndefinedOrNull);

        return renderToStaticMarkup(
          <KeyValueTooltip x={format(new Date(Number(this.x)), "MMM yyyy")} keyValues={keyValues} />,
        );
      },
    },
    plotOptions: {
      series: {
        events: {
          legendItemClick(event) {
            handleClickInLegend(event, this, chartRef);
          },
        },
      },
      column: {
        clip: true,
        borderRadius: {
          where: "end",
          radius: 8,
          scope: "stack",
        },
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
  };

  if (loading && quotaholdersHistory.length === 0) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  return (
    <OnlyPremiumRender>
      <div className="flex h-full w-full flex-col space-y-4">
        <HighchartsChart ref={chartRef} options={options} className="h-96 xl:h-full" />
      </div>
    </OnlyPremiumRender>
  );
}
